<template>
	<div class="sheet-settings">

		<!-- Sheet settings -->
		<section class="card">
			<div class="card-content">
				<form :disabled="sheet.closed">
					<div class="card-body row">
						<fieldset :disabled="anonymousMode && shareEnabled" class="col-md-6">
							<!-- Name -->
							<div class="form-group">
								<label v-i18n="'sheet_settings_sheetTitle'" class="form-control-label"></label>
								<input v-model="name" class="form-control" :disabled="sheet.closed"/>
							</div>
							<!-- Currency -->
							<div class="form-group" v-if="anonymousMode">
								<label v-i18n="'book_settings_currency'" class="form-control-label"></label>
								<input v-model="currency" class="form-control" required/>
							</div>
							<!-- Public -->
							<div v-if="!anonymousMode">
								<div class="form-check">
									<label class="form-check-label">
										<input type="checkbox" class="form-check-input" v-model="shareEnabled">
										{{$formatMessage('sheet_share_enableSharing')}}
									</label>
									<small class="form-text text-muted" v-i18n="'sheet_share_enableSharing_tip'"></small>
								</div>
								<fieldset :disabled="!shareEnabled" v-show="shareEnabled">
									<div class="form-group">
										<label for="sheet-share-url" v-i18n="'sheet_share_linkSheet'"></label>
										<input id="sheet-share-url" class="form-control" v-model="shareUrl" readonly="readonly">
										<small class="form-text text-muted" v-i18n="'sheet_share_linkSheet_tip'"></small>
									</div>
									<div class="form-group">
										<select v-model="shareMode" class="form-control">
											<option value="1" v-i18n="'sheet_share_mode_view'"></option>
											<option value="2" v-i18n="'sheet_share_mode_edit'"></option>
										</select>
									</div>
								</fieldset>
							</div>
						</fieldset>
						<!-- Default split -->
						<fieldset :disabled="(shareEnabled && anonymousMode) || sheet.closed" class="col-md-6" v-if="split">
							<h4 v-i18n="'sheet_settings_split_title'"></h4>
							<small class="form-text text-muted" v-i18n="'sheet_settings_split_help'"></small>
							<input-split :split="split" :sheet="sheet" :disabled="sheet.closed" :showCheckbox="false"></input-split>
						</fieldset>
					</div>
					<!-- Buttons -->
					<div class="card-footer" v-if="sheet.isOpen && !(shareEnabled && anonymousMode)">
						<button type="button" v-i18n="'save'" class="card-link" :disabled="cannotSubmit"
						        @click="saveSheet"></button>
					</div>
				</form>
			</div>
		</section>

		<!-- Participants -->
		<section class="participants" v-if="sheet.id && !(anonymousMode && shareEnabled)">
			<!-- Title and add button -->
			<div class="row">
				<h3 v-i18n="'sheet_settings_sheeters'" class="col-8"></h3>
				<div class="col-4 text-right" v-if="!sheet.closed">
					<button type="button" class="btn btn-primary" v-i18n="'add'"
					        @click="$emit('addParticipants')"></button>
				</div>
			</div>
			<!-- List of sheeters -->
			<div class="row margin-top">
				<div class="col-md-6" v-for="item of sortedItems" :key="item.sheeter.id">
					<!-- Sheeter card -->
					<card class="card-avatar">
						<!-- Avatar -->
						<avatar :booker="item.booker"></avatar>
						<div class="row">
							<!-- Name -->
							<h4 class="col-6 no-margin text-nowrap text-truncate" :title="item.booker.name">
								{{item.booker.name}}</h4>
							<!-- Expenses count -->
							<div class="col-6 text-secondary">
								∑ {{item.sumExpenses | format-currency(currency)}}
							</div>
						</div>
						<!-- Buttons -->
						<div slot="footer" class="card-footer">
							<!-- Rename -->
							<button type="button" class="card-link"
							        @click="openBookerNameModal(item.booker)"
							        v-if="anonymousMode">
								{{'rename' | format-message}}
							</button>
							<!-- Remove -->
							<button type="button" class="card-link" v-if="!item.isMyself"
							        @click="removeSheeter(item.sheeter)"
							        :title="item.sumExpenses > 0 ? $formatMessage('sheeter_cannotRemove') : ''"
							        :disabled="sheet.closed || item.sumExpenses > 0 || removingChild">
								{{'delete' | format-message}}
							</button>
						</div>
					</card>
				</div>
			</div>
		</section>
		<modal title="booker_changeName_modal_title" ref="modalBookerName"
		       modal-class="modal-booker-name"
		       :disabled="renamingBooker" :applyDisabled="!editedBookerName"
		       v-on:apply="applyBookerName">
			<input type="text" v-model="editedBookerName" ref="inputBookerName" class="form-control"/>
		</modal>
	</div>
</template>

<script>
	import store, {ChildrenObserver} from "../core/store";
	import {HasSplit} from "../core/entities";
	import store_utils from "../utils/store";
	import Avatar from "../ui/widgets/avatar.vue";
	import Card from "../ui/widgets/card.vue";
	import IconButton from "../ui/widgets/icon-button.vue";
	import InputSplit from '../ui/widgets/input-split.vue';
	import Modal from '../ui/widgets/modal.vue';
	import toast from "../ui/toast";
	import PimplWrapper from "../utils/pimpl"

	class Item {
		constructor(sheeter, sumExpenses) {
			this.sheeter = sheeter;
			this.booker = sheeter.booker;
			this.sumExpenses = sumExpenses;
			this.isMyself = this.booker.isMyself();
		}
	}

	class Pimpl extends ChildrenObserver {
		constructor(component) {
			super(component.sheet.key);
			this.component = component;
		}
		resetSplit() {
			let component = this.component;
			component.split = component.sheet.cloneSplit() || component.sheet.createSplit();
		}

		onAdded(event, params) {
			let item;
			let component = this.component;
			for (let entity of params.entities) {
				if (entity.kind === 'Sheeter') {
					item = new Item(entity, 0);
					component.items.unshift(item);
				}
			}
			if (item) { // a sheeter has been added
				setTimeout(() => { // defer reset: sheeter added to store before sheet updated and we need the updated balance to get the bookers
					this.resetSplit();
				}, 0)
			}
		}

		onRemoved(event, params) {
			if (params.kind === 'Sheeter') {
				let component = this.component, sheetersRemoved = false;
				for (let id of params.ids) {
					let index = component.items.findIndex((item) => item.sheeter.id === id);
					if (index !== -1) {
						component.items.splice(index, 1);
						sheetersRemoved = true;
					}
				}
				if (sheetersRemoved) {
					this.resetSplit();
				}
			}
		}
	}

	export default {
		props: {
			sheet: Object,
			anonymousMode: {'type': Boolean, 'default': false}
		},

		components: {
			Avatar,
			InputSplit,
			Modal,
			Card
		},

		data() {
			// Load sheeters
			const sheet = this.sheet;
			let items;
			if (sheet.balance) {
				const balance = sheet.balance.reduce((map, balance) => {
					map.set(balance.id, balance.value[0]);
					return map;
				}, new Map());
				items = sheet.sheeters.map(sheeter => new Item(sheeter, balance.get(sheeter.booker_id)));
			} else {
				items = sheet.sheeters.map(sheeter => new Item(sheeter, 0));
			}

			// Share URL
			const shareMode = sheet.share_mode || 0;
			const shareUrl = [location.protocol, '//', location.host, '/share/'];
			for (let segment of sheet.key.segments()) {
				const keyStr = segment.id.toString(16);
				const keyLenStr = ('0'+keyStr.length).slice(-2);
				shareUrl.push(keyLenStr);
				shareUrl.push(keyStr);
			}

			return {
				p: new PimplWrapper(new Pimpl(this)),
				savingSheet: false,
				items,
				split: this.sheet.id ? this.sheet.cloneSplit() || this.sheet.createSplit() : null,
				name: this.sheet.name,
				editedBooker: null,
				editedBookerName: "",
				renamingBooker: false,
				removingChild: false,
				currency: this.sheet.parent.currency,
				shareMode,
				shareEnabled: shareMode > 0,
				shareUrl: shareUrl.join(''),
				savingShare: false,
			}
		},

		computed: {
			sortedItems() {
				return this.items.sort((itemA, itemB) => {
					return itemA.booker.name.localeCompare(itemB.booker.name);
				})
			},
			cannotSubmit() {
				let canSubmit = !this.savingSheet && Boolean(this.currency);
				if (canSubmit) {
					const split = HasSplit.copySplit(this.split);
					canSubmit = split === null || split.valid;
				}
				return !canSubmit;
			}
		},

		mounted() {
			this.p.impl.register();
		},

		beforeDestroy() {
			this.$refs.modalBookerName.hide();
		},

		destroyed() {
			this.p.impl.unregister();
		},

		methods: {
			saveSheet() {
				this.savingSheet = true;
				let afterSave = () => {
					this.savingSheet = false
				};

				// Update sheet
				let sheet = this.sheet.clone();
				sheet.name = this.name;
				if (!this.anonymousMode)
					sheet.share_mode = this.shareMode || 0; // settings shareMode to 0 in $watch seems to set it to undefined
				sheet.assignSplit(this.split);
				if (sheet.split.type === "everybody") {
					sheet.split = null;
				}
				let acknowledge = () => {
					toast.tr('sheet_settings_saved')
				};
				sheet.update()
					.then(() => {
						let book = this.sheet.parent;
						if (this.currency !== book.currency) {
							let bookClone = book.clone();
							bookClone.currency = this.currency;
							bookClone.update().then(afterSave, afterSave).then(acknowledge);
						} else {
							afterSave();
							acknowledge();
						}
					}, afterSave);

			},
			openBookerNameModal(booker) {
				this.editedBooker = booker.clone();
				this.editedBookerName = booker.name;
				this.$refs.modalBookerName.show();
			},
			applyBookerName() {
				this.renamingBooker = true;
				let afterSave = () => {
					this.renamingBooker = false;
					this.$refs.modalBookerName.hide();
				};
				this.editedBooker.name = this.editedBookerName;
				this.editedBooker.update()
					.always(afterSave)
					.then(() => {
						toast.tr('booker_nameChanged');
					});
			},
			removeSheeter(sheeter) {
				let booker = sheeter.booker;
				if (confirm(this.$formatMessage('sheeter_remove_confirm', {name: booker.name}))) {
					let afterSave = () => {
						this.removingChild = false;
					};
					let promise = sheeter.parent.removeChildren('Sheeter', [sheeter.id], {doNotCatch: true}).catch(response => {
						if (response.status === 412) {
							toast.error(this.$formatMessage('sheeter_cannotRemove'));
						} else {
							toast.error(response.statusText + "<br/>" + response.responseText);
						}
					});
					if (this.anonymousMode) {
						promise.then(() => {
							this.sheet.parent.removeChildren('Booker', [booker.id])
								.then(afterSave, afterSave)
						});
					} else {
						promise.then(afterSave, afterSave);
					}
				}
			},
			openBookersModal() {
				this.$refs.modalBookers.show();
			}
		},

		watch: {
			'sheet.split': function () {
				this.p.impl.resetSplit();
			},
			'sheet.share_mode'(shareMode) {
				this.shareEnabled = shareMode > 0;
			},
			'shareEnabled'() {
				if (Boolean(this.shareMode) != this.shareEnabled) {
					this.shareMode = this.shareEnabled ? 2 : 0;
				}
			}
		}
	}
</script>

<style lang="scss">
	.sheet-settings section.participants {
		position: relative;
		margin-top: 2rem;
	}
</style>



// WEBPACK FOOTER //
// src/ui/sheet-settings.vue